import { useEffect, useState } from 'react';

import { useAuth } from '@opendoor/auth-fe';
import type { OdProtosCustomerV1Services_CustomerRequests_Update_ParamsInput } from '__generated__/athena';

import { getAthenaClient, getFeatureFlag } from '../api';
import type { Customer, UseCurrentCustomer } from './types';

/**
 * Development/QA localstorage feature flag override.
 *
 * force enabled -> window.localStorage.setItem('opendoor.cosmos.<flag>', 'true')
 *
 * Returns 'on' when overridden, else null, so the override path is shape-
 * compatible with the BFF variant strings ('on' | 'control' | ...) that
 * consumers compare against.
 */
export const getLocalFeatureFlagOverrides = (featureFlag: string): string | null => {
  try {
    // Ignore override check on the server
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(`opendoor.cosmos.${featureFlag}`) === 'true' ? 'on' : null;
  } catch {
    return null;
  }
};

type UseGetFeatureFlag = (...p: Parameters<typeof getFeatureFlag>) => string;
export const useGetFeatureFlag: UseGetFeatureFlag = (
  name,
  customerUUID = '',
  options = {},
  anonId = '',
) => {
  const [featureFlag, setFeatureFlag] = useState(options.defaultValue || '');

  useEffect(() => {
    getFeatureFlag(name, customerUUID, options, anonId).then(setFeatureFlag);
  }, []);

  return featureFlag;
};

/**
 * Gets the customer data for the currently logged in user and provides a method to update the current cutomer's data.
 */
export const useCurrentCustomer: UseCurrentCustomer = (options = {}) => {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const {
    authentication: { state: authState },
  } = useAuth();

  useEffect(() => {
    if (authState !== 'authenticated') {
      setCustomer(null);
      setIsLoading(false);
      return;
    }

    const getCustomer = async () => {
      setIsLoading(true);
      try {
        const customerResp = await getAthenaClient().GetCurrentCustomer();
        setCustomer(
          (customerResp.customerService.getCurrentCustomer?.customer as Customer) || null,
        );
      } catch (e) {
        setCustomer(null);
        if (!options.onGetError) throw e;

        options.onGetError(e);
      } finally {
        setIsLoading(false);
      }
    };

    getCustomer();
  }, [authState]);

  const update = async (params: OdProtosCustomerV1Services_CustomerRequests_Update_ParamsInput) => {
    if (!customer) return;

    setIsUpdating(true);
    try {
      const resp = await getAthenaClient().UpdateCurrentCustomer({
        customerUpdates: params,
      });

      const updatedCustomer = resp.customerService.updateCurrent?.updatedCustomer as Customer;

      setCustomer(updatedCustomer);
      return updatedCustomer;
    } catch (e) {
      if (!options.onUpdateError) throw e;

      options.onUpdateError(e);
      return;
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    customer,
    isLoading,
    isUpdating,
    isUserAuthenticated: authState === 'authenticated',
    update,
  };
};
